import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { surveyApi, projectsApi } from '@/lib/apiClient';
import { StateView } from '@/components';
import { fmtDateTime } from '@/lib/dates';
import { SurveyStatusBadge } from '../survey/statusBadge';

// E&W's Formstack survey-services intake form. Submissions flow back into the
// survey list via the survey-request webhook — Formstack is the only inbound
// path, so we deliberately don't fork a second, in-app create form here. Same
// URL the global Survey page links to.
const FORMSTACK_URL = 'https://redeapp.formstack.com/forms/survey_services_request_form_copy_1';

interface ProjectRef { id: number; jobNumber: string; title: string }

interface SurveyTaskRef { planned?: boolean; taskType?: { label?: string | null } | null }
interface SurveyRequestRow {
  id: number;
  status: string;
  siteVisitDateTime: string | null;
  duration: string | null;
  siteReady: string | null;
  tasks?: SurveyTaskRef[];
}

// Mirror the RFI/Receiving table look so the tab feels native in the workspace.
const css = `
  .svy-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .svy-table th {
    text-align: left; padding: 10px var(--ew-s-4); white-space: nowrap;
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500);
    border-bottom: 1px solid var(--ew-n-100);
  }
  .svy-table td { padding: 11px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: top; }
  .svy-table tr:last-child td { border-bottom: 0; }
  .svy-muted { color: var(--ew-n-500); }
  .svy-hint { font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-n-600); max-width: 62ch; }
  .svy-link { color: var(--ew-green-700); font-weight: var(--ew-fw-semibold); text-decoration: none; white-space: nowrap; }
  .svy-link:hover { text-decoration: underline; }
`;

export default function SurveyTab({ projectId }: { projectId: number }) {
  // This job's survey requests. Scoped server-side via the new ?projectId=
  // filter (precise — the global page's job LIKE could match neighbours).
  const query = useQuery({
    queryKey: ['survey-requests', projectId],
    queryFn: () => surveyApi.listRequests({ projectId: String(projectId) }) as Promise<SurveyRequestRow[]>,
  });

  // Job number to remind the requester what to type on the Formstack form
  // (it can't be reliably pre-filled without its field IDs). Shares the warm
  // workspace ['project', id] cache.
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<ProjectRef>,
    enabled: Number.isFinite(projectId),
    staleTime: 60_000,
  });
  const jobNumber = projectQuery.data?.jobNumber ?? '';

  return (
    <>
      <style>{css}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--ew-s-4)', marginBottom: 'var(--ew-s-4)', flexWrap: 'wrap' }}>
        <span className="svy-hint">
          Survey requests are submitted through the Formstack intake form and flow back here automatically.
          {jobNumber ? <> Enter job <strong>#{jobNumber}</strong> on the form.</> : null}
        </span>
        <a className="btn btn-primary" href={FORMSTACK_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}>
          + Survey Services Request
        </a>
      </div>

      <div className="pw-card">
        <h2 className="pw-card-hd">Survey requests</h2>
        <StateView query={query} empty="No survey requests for this job yet. Submit one with the button above.">
          {(requests) => (
            <div className="ew-table-scroll">
            <table className="svy-table ew-cards">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Site visit</th>
                  <th>Duration</th>
                  <th>Scope</th>
                  <th>Site ready?</th>
                  <th aria-label="Detail" />
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => {
                  const scope = (r.tasks ?? [])
                    .filter((t) => t.planned)
                    .map((t) => t.taskType?.label)
                    .filter((l): l is string => !!l);
                  return (
                    <tr key={r.id}>
                      <td data-label="Status"><SurveyStatusBadge label={r.status} /></td>
                      <td className="svy-muted" data-label="Site visit" style={{ whiteSpace: 'nowrap' }}>{r.siteVisitDateTime ? fmtDateTime(r.siteVisitDateTime) : '—'}</td>
                      <td className="svy-muted" data-label="Duration">{r.duration ?? '—'}</td>
                      <td className="svy-muted" data-label="Scope">{scope.length ? scope.join(', ') : '—'}</td>
                      <td className="svy-muted" data-label="Site ready?">{r.siteReady ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <Link className="svy-link" to={`/survey?request=${r.id}`}>View →</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </StateView>
      </div>
    </>
  );
}
